import { useTranslation } from "react-i18next";
import { translate as tr } from "../../i18n";
import React, { useEffect, useState } from "react";
import SideBar from "../common/sidebar";
import Footer from "../common/footer";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { loadUser } from "../../actions/auth";
import "../../assets/css/dashboard.css";

export default function WorkspaceManage() {
  useTranslation();
  const auth     = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const history = useNavigate();

  const [workspaces, setWorkspaces]     = useState([]);
  const [editingId, setEditingId]       = useState(null);
  const [editTitle, setEditTitle]       = useState("");
  const [editError, setEditError]       = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving]             = useState(false);
  const [deleting, setDeleting]         = useState(false);
  const [successMsg, setSuccessMsg]     = useState("");
  const [errorMsg, setErrorMsg]         = useState("");

  const authConfig = {
    headers: {
      Authorization: `Bearer ${auth.token}`,
      "Content-Type": "application/json",
    },
  };

  // Decode base64 user_id once
  const myUserId = React.useMemo(() => {
    try { return parseInt(atob(auth.user_id || ""), 10); }
    catch { return null; }
  }, [auth.user_id]);

  useEffect(() => { fetchWorkspaces(); }, []);

  const fetchWorkspaces = async () => {
    try {
      const res = await axios.post(
        "/api/workspace/listbyworkspaceid",
        JSON.stringify({ workspace_id: auth.workspace_id }),
        authConfig
      );
      if (res.status === 200) setWorkspaces(res.data.results);
    } catch { setErrorMsg("Failed to load workspaces."); }
  };

  const startEdit = (ws) => {
    setEditingId(ws.id);
    setEditTitle(ws.workspace_title || ws.title || "");
    setEditError("");
  };

  const cancelEdit = () => { setEditingId(null); setEditTitle(""); setEditError(""); };

  const saveEdit = async (workspace_id) => {
    if (!editTitle.trim()) { setEditError("Name cannot be empty."); return; }
    setSaving(true);
    try {
      await axios.put("/api/workspace/edit", JSON.stringify({ workspace_id, title: editTitle.trim() }), authConfig);
      setWorkspaces(prev => prev.map(ws =>
        ws.id === workspace_id ? { ...ws, workspace_title: editTitle.trim(), title: editTitle.trim() } : ws
      ));
      setEditingId(null);
      flash("Workspace renamed successfully.");
    } catch (err) {
      setEditError(err.response?.data?.msg || "Failed to save.");
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setErrorMsg("");
    try {
      const res = await axios.delete("/api/workspace/delete", {
        ...authConfig,
        data: JSON.stringify({ workspace_id: deleteTarget.id }),
      });
      setDeleteTarget(null);
      if (res.data.workspace_id) {
        dispatch(loadUser(res));
      }
      flash("Workspace deleted.");
      fetchWorkspaces();
    } catch (err) {
      setErrorMsg(err.response?.data?.msg || "Failed to delete workspace.");
    } finally { setDeleting(false); }
  };

  const switchWorkspace = async (workspace_id) => {
    try {
      const res = await axios.post("/api/workspace/select", JSON.stringify({ workspace_id }), authConfig);
      if (res.status === 200) { dispatch(loadUser(res)); history("/dashboard"); }
    } catch { setErrorMsg("Failed to switch workspace."); }
  };

  const flash = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(""), 3000);
  };

  const getInitials = (title) => {
    if (!title) return "W";
    return title.split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase();
  };

  return (
    <div>
      <SideBar />
      <div className="content-wrap wm-layout" id="wrapper">
        <div className="main wm-main">

          {/* ── Page header ── */}
          <div className="wm-page-header">
            <div>
              <h1 className="wm-page-title">{tr("Workspaces")}</h1>
              <p className="wm-page-sub">{tr("Manage and switch between your workspaces")}</p>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button className="wm-btn-settings" onClick={() => history("/settings")}>
                <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
                  <path d="M8 10a2 2 0 100-4 2 2 0 000 4z" stroke="currentColor" strokeWidth="1.4"/>
                  <path d="M13.3 7.1l.9-.3V7v2l-.9-.3a5.4 5.4 0 01-.5.9l.4.8-1.4 1.4-.8-.4a5.4 5.4 0 01-.9.5l-.3.9H8.1l-.3-.9a5.4 5.4 0 01-.9-.5l-.8.4-1.4-1.4.4-.8a5.4 5.4 0 01-.5-.9L4 9V7l.6.2a5.4 5.4 0 01.5-.9l-.4-.8 1.4-1.4.8.4a5.4 5.4 0 01.9-.5L7.9 4h.2l.9.3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                </svg>{tr("Settings")} </button>
              <button className="wm-btn-create" onClick={() => history("/workspace")}>{tr("+ New Workspace")} </button>
            </div>
          </div>

          {/* ── Alerts ── */}
          {successMsg && <div className="wm-flash wm-flash--ok">{successMsg}</div>}
          {errorMsg && !deleteTarget && <div className="wm-flash wm-flash--err">{errorMsg}</div>}

          {/* ── Workspace cards ── */}
          <div className="wm-grid">
            {workspaces.map((ws) => {
              const isActive = ws.id === auth.workspace_id;
              const isOwner  = ws.user_id === myUserId;
              const name     = ws.workspace_title || ws.title || tr("Untitled");

              return (
                <div key={ws.id} className={`wm-card${isActive ? " wm-card--active" : ""}`}>

                  {/* Active indicator bar */}
                  {isActive && <div className="wm-card__bar" />}

                  {/* Avatar + info */}
                  <div className="wm-card__top">
                    <div className={`wm-card__avatar${isActive ? " wm-card__avatar--active" : ""}`}>
                      {getInitials(name)}
                    </div>
                    <div className="wm-card__meta">
                      {editingId === ws.id ? (
                        <div className="wm-inline">
                          <input
                            className={`wm-inline__input${editError ? " wm-inline__input--err" : ""}`}
                            value={editTitle}
                            onChange={e => setEditTitle(e.target.value)}
                            onKeyDown={e => { if (e.key === "Enter") saveEdit(ws.id); if (e.key === "Escape") cancelEdit(); }}
                            autoFocus
                          />
                          {editError && <span className="wm-inline__err">{editError}</span>}
                        </div>
                      ) : (
                        <span className="wm-card__name">{name}</span>
                      )}
                      <div className="wm-card__badges">
                        {isActive && <span className="wm-badge wm-badge--active">{tr("Active")}</span>}
                        {isOwner  && <span className="wm-badge wm-badge--owner">{tr("Owner")}</span>}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="wm-card__actions">
                    {editingId === ws.id ? (
                      <>
                        <button className="wm-btn wm-btn--save" onClick={() => saveEdit(ws.id)} disabled={saving}>
                          {saving ? tr("Saving…") : tr("Save")}
                        </button>
                        <button className="wm-btn wm-btn--cancel" onClick={cancelEdit}>{tr("Cancel")}</button>
                      </>
                    ) : (
                      <>
                        {!isActive && (
                          <button className="wm-btn wm-btn--switch" onClick={() => switchWorkspace(ws.id)}>{tr("Switch")} </button>
                        )}
                        <button className="wm-btn wm-btn--edit" onClick={() => startEdit(ws)}>{tr("Rename")} </button>
                        {isOwner && workspaces.length > 1 && (
                          <button className="wm-btn wm-btn--delete" onClick={() => setDeleteTarget(ws)}>{tr("Delete")} </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Delete confirmation modal ── */}
        {deleteTarget && (
          <div className="wm-overlay" onClick={() => { setDeleteTarget(null); setErrorMsg(""); }}>
            <div className="wm-modal" onClick={e => e.stopPropagation()}>
              <div className="wm-modal__icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                  <path d="M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke="#e20505" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3 className="wm-modal__title">{tr("Delete workspace?")}</h3>
              <p className="wm-modal__body">{tr("You are about to permanently delete")} <strong>{deleteTarget.workspace_title || deleteTarget.title}</strong>{tr(". All projects, timers, and team members in this workspace will be removed. This cannot be undone.")} </p>
              {errorMsg && <div className="wm-flash wm-flash--err" style={{ marginBottom: 12 }}>{errorMsg}</div>}
              <div className="wm-modal__footer">
                <button className="wm-btn wm-btn--cancel" onClick={() => { setDeleteTarget(null); setErrorMsg(""); }} disabled={deleting}>{tr("Cancel")} </button>
                <button className="wm-btn wm-btn--confirm-delete" onClick={doDelete} disabled={deleting}>
                  {deleting ? tr("Deleting…") : tr("Yes, delete")}
                </button>
              </div>
            </div>
          </div>
        )}

        <Footer />
      </div>
    </div>
  );
}
